import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  getQuiz,
  submitDoshaQuiz,
} from "./index.js";
import "./DoshaQuiz.css";
import {
  initializeDoshaQuizGTM,
  trackPageView,
  trackQuizEvent,
  trackFormEvent,
  trackEngagement,
  trackConversion,
  trackError
} from "../../utils/gtm";
import {
  captureUtmAttribution,
  captureLocationAsync,
  buildAttributionPayload,
} from "../../utils/utmAttribution";

// Global constants for answer types
const RADIO_BUTTON = "radio_button";
const MULTI_SELECT = "multi_select";
const TEXT_FIELD = "text_field";

/** Matches API content_type for checkbox + rich label/link (same UI as privacy policy). */
const POLICY_LIKE_FORM_TYPES = ["privacypolicy", "consentcheckbox"];

/** Quiz API may return content_managements under payload.data or at the payload root. */
function getContentManagementsFromPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  const nested = payload.data?.content_managements;
  if (Array.isArray(nested)) return nested;
  const root = payload.content_managements;
  if (Array.isArray(root)) return root;
  return null;
}

function normalizeFormContentType(field) {
  const raw = field?.content_type ?? field?.contentType;
  return String(raw ?? "").trim().toLowerCase();
}

/** CMS field names may include stray whitespace (e.g. "name "); trim so form state keys stay canonical. */
function normalizeFormFieldName(name) {
  return String(name ?? "").trim();
}

const QUIZ_FORM_LEFT_IMAGE_BY_UID = {
  MjM0: "/quiz_us.png",
};

/** 0 = normal inputs, 1 = privacy policy, 2 = extra consents (always after privacy). */
function formFieldSortTier(field) {
  const t = normalizeFormContentType(field);
  if (t === "privacypolicy") return 1;
  if (t === "consentcheckbox") return 2;
  return 0;
}

const DoshaQuiz = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    month: "",
    day: "",
    year: "",
    gender: "",
    ayurvedaOther: "",
    privacyPolicy: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizResponses, setQuizResponses] = useState({});
  const [currentCategory, setCurrentCategory] = useState(0);
  const [quizProgress, setQuizProgress] = useState({});
  const [token, setToken] = useState(null);
  const [finalResponse, setFinalResponse] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [agentId, setAgentId] = useState(
    "dosha_quiz" + Math.floor(Math.random() * 1000000)
  );
  const [sessionId, setSessionId] = useState(
    "dosha_quiz" + Math.floor(Math.random() * 1000000)
  );
  const [quizData, setQuizData] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dominantDosha, setDominantDosha] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [location, setLocation] = useState("");
  const [language, setLanguage] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const currentQuestionIndexRef = useRef(0);
  const quizResponsesRef = useRef({});
  const isManualNavigationRef = useRef(false);
  const [showTooltipForQuestion, setShowTooltipForQuestion] = useState(null);


  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const years = Array.from({ length: 100 }, (_, i) => 2024 - i);


  const decodeUid = (encodedUid) => {
    try {
      if (!encodedUid) return null;

      if (
        encodedUid.includes("=") ||
        /^[A-Za-z0-9+/]*={0,2}$/.test(encodedUid)
      ) {
        const decoded = atob(encodedUid);
        return decoded;
      }

      return encodedUid;
    } catch (error) {
      console.error("Error decoding UID:", error);
      return encodedUid;
    }
  };

  useEffect(() => {
    captureUtmAttribution();
    captureLocationAsync();

    const urlParams = new URLSearchParams(window.location.search);
    const languageFromUrl = urlParams.get("language");

    if (!quizData) {
      fetchQuizData(uid, languageFromUrl || null);
    }
    
    trackPageView('Dosha Quiz', window.location.pathname);
    initializeDoshaQuizGTM();
  }, [uid]);

  useEffect(() => {
    const customFaviconUrl = 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://maharishiayurvedaindia.com&size=64';
    const defaultFaviconUrl = '/favicon.ico';
    
    const pageTitle = 'Know Your Dominant Dosha & Start Your Ayurvedic Wellness Journey Today';
    const pageDescription = 'Take the Ayurvedic Dosha Quiz and get instant insights into your unique body type. Understand your Ama (toxin buildup), receive diet and lifestyle guidance, and explore personalized remedies to restore balance.';
    
    const originalTitle = document.title;
    const originalDescription = document.querySelector('meta[name="description"]')?.getAttribute('content');
    
    document.title = pageTitle;
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', pageDescription);
    
    const setMetaProperty = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    const currentUrl = window.location.href;
    setMetaProperty('og:title', pageTitle);
    setMetaProperty('og:description', pageDescription);
    setMetaProperty('og:type', 'website');
    setMetaProperty('og:url', currentUrl);
    
    const setMetaName = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', pageTitle);
    setMetaName('twitter:description', pageDescription);
    
    const existingLinks = Array.from(document.querySelectorAll("link[rel*='icon']"));
    const originalHrefs = existingLinks.map(link => ({
      element: link,
      href: link.href,
      rel: link.getAttribute('rel')
    }));
    
    existingLinks.forEach(link => {
      link.href = customFaviconUrl;
    });
    
    if (existingLinks.length === 0) {
      const faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.type = 'image/x-icon';
      faviconLink.href = customFaviconUrl;
      document.head.appendChild(faviconLink);
    }

    return () => {
      document.title = originalTitle;
      const descMeta = document.querySelector('meta[name="description"]');
      if (descMeta && originalDescription) {
        descMeta.setAttribute('content', originalDescription);
      }
      
      const linksToRestore = document.querySelectorAll("link[rel*='icon']");
      linksToRestore.forEach((link, index) => {
        if (originalHrefs[index]) {
          link.href = originalHrefs[index].href;
        } else {
          link.href = defaultFaviconUrl;
        }
      });
    };
  }, []);


  // Update refs when state changes
  useEffect(() => {
    currentQuestionIndexRef.current = currentQuestionIndex;
  }, [currentQuestionIndex]);

  useEffect(() => {
    quizResponsesRef.current = quizResponses;
  }, [quizResponses]);


  useEffect(() => {
    if (showQuiz && quizData?.data?.categories && currentCategory >= 0) {
      // Small delay to ensure DOM has updated with new category questions
      const timer = setTimeout(() => {
        // Only adjust if not manually navigating and index is invalid
        if (!isManualNavigationRef.current) {
          const currentCat = quizData.data.categories[currentCategory];
          if (currentCat && currentCat.questions && currentCat.questions.length > 0) {
            // Check if current index is valid for new category
            const currentIndex = currentQuestionIndexRef.current;
            if (currentIndex >= currentCat.questions.length || currentIndex < 0) {
              currentQuestionIndexRef.current = 0;
              setCurrentQuestionIndex(0);
            }
          }
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [currentCategory, showQuiz, quizData]);

  // Hide tooltip when question is answered
  useEffect(() => {
    if (showTooltipForQuestion && quizResponses[showTooltipForQuestion]) {
      const question = quizData?.data?.categories?.[currentCategory]?.questions?.find(
        q => q.id === showTooltipForQuestion
      );
      if (question) {
        const response = quizResponses[showTooltipForQuestion];
        let isAnswered = false;
        
        if (question.answer_type === TEXT_FIELD) {
          isAnswered = response.answer && response.answer.trim().length > 0;
        } else if (question.answer_type === MULTI_SELECT) {
          if (response.answer) {
            if (Array.isArray(response.answer)) {
              isAnswered = response.answer.length > 0;
            } else {
              isAnswered = response.answer.split(",").filter((opt) => opt.trim().length > 0).length > 0;
            }
          }
        } else {
          isAnswered = response.score !== undefined;
        }
        
        if (isAnswered) {
          setShowTooltipForQuestion(null);
        }
      }
    }
  }, [quizResponses, showTooltipForQuestion, currentCategory, quizData]);

  // Scroll-based question number tracking
  useEffect(() => {
    if (!showQuiz || !quizData?.data?.categories) return;

    const container = document.querySelector('.quiz-full-width');
    if (!container) return;

    // Small delay to ensure DOM has updated with new category questions
    let cleanupFn = null;
    const timer = setTimeout(() => {
      const questionItems = document.querySelectorAll('.dosha-section .question-item');
      if (questionItems.length === 0) return;

      const observerOptions = {
        root: container,
        rootMargin: '-20% 0px -50% 0px', // Trigger when question is in the upper portion of viewport
        threshold: 0.1
      };

      const observerCallback = (entries) => {
        // Skip updates during manual navigation
        if (isManualNavigationRef.current) return;
        
        // Find the question that is most visible in the viewport
        let mostVisibleEntry = null;
        let maxIntersection = 0;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > maxIntersection) {
            maxIntersection = entry.intersectionRatio;
            mostVisibleEntry = entry;
          }
        });

        if (mostVisibleEntry) {
          const questionIndex = Array.from(questionItems).indexOf(mostVisibleEntry.target);
          if (questionIndex !== -1 && questionIndex !== currentQuestionIndexRef.current) {
            setCurrentQuestionIndex(questionIndex);
          }
        }
      };

      const observer = new IntersectionObserver(observerCallback, observerOptions);

      // Observe all question items
      questionItems.forEach((item) => {
        observer.observe(item);
      });

      // Also handle scroll events as a fallback
      const handleScroll = () => {
        // Skip updates during manual navigation
        if (isManualNavigationRef.current) return;
        
        const headerEl = document.querySelector('.quiz-header');
        const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
        const viewportTop = container.scrollTop + headerHeight + 100;

        let closestIndex = 0;
        let closestDistance = Infinity;

        questionItems.forEach((item, index) => {
          const itemTop = item.offsetTop;
          const distance = Math.abs(itemTop - viewportTop);
          
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== currentQuestionIndexRef.current) {
          setCurrentQuestionIndex(closestIndex);
        }
      };

      container.addEventListener('scroll', handleScroll, { passive: true });

      cleanupFn = () => {
        observer.disconnect();
        container.removeEventListener('scroll', handleScroll);
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      if (cleanupFn) cleanupFn();
    };
  }, [showQuiz, quizData, currentCategory]);


  const fetchQuizData = async (uid, languageName = null) => {
    localStorage.removeItem("doshaCompleted");
    try {
      setLoading(true);
      setError(null);
      const response = await getQuiz(uid, languageName);
      setQuizData(response);
      setApiAvailable(true);
      
      if (response?.data?.token) {
        setToken(response.data.token);
      }
      
      if (response?.data?.location) {
        setLocation(response.data.location);
      }
      if (response?.data?.language) {
        setLanguage(response.data.language);
      }
      
      const cmsList = getContentManagementsFromPayload(response);
      if (cmsList) {
        const policyLikeFields = cmsList.filter(
          (field) => POLICY_LIKE_FORM_TYPES.includes(normalizeFormContentType(field)) && field.name
        );
        if (policyLikeFields.length > 0) {
          setFormData((prev) => {
            const next = { ...prev };
            policyLikeFields.forEach((f) => {
              next[normalizeFormFieldName(f.name)] = false;
            });
            return next;
          });
        }

        const languageField = cmsList.find((field) => normalizeFormContentType(field) === "language");
        if (languageField && languageField.options && languageField.options.length > 0) {
          const apiLanguage = response?.data?.language;
          let selectedLanguage = null;
          
          if (apiLanguage) {
            const matchingOption = languageField.options.find(
              opt => opt.option === apiLanguage
            );
            if (matchingOption) {
              selectedLanguage = matchingOption.option;
            }
          }
          
          if (!selectedLanguage) {
            const firstOption = languageField.options
              .sort((a, b) => (a.sequance || 0) - (b.sequance || 0))[0];
            if (firstOption && firstOption.option) {
              selectedLanguage = firstOption.option;
            }
          }
          
          if (selectedLanguage) {
            setFormData((prev) => ({
              ...prev,
              [normalizeFormFieldName(languageField.name)]: selectedLanguage,
            }));
          }
        }
      }
      
      trackQuizEvent('quiz_data_loaded', {
        quiz_uid: uid,
        categories_count: response?.data?.categories?.length || 0,
        has_token: !!response.data.token,
        language: languageName || response.data.language
      });
    } catch (err) {
      console.error("Error fetching quiz data:", err);
      setError(err.message || "Failed to fetch quiz data");
      setApiAvailable(false);
      
      trackError('quiz_data_fetch_error', err.message || "Failed to fetch quiz data", {
        quiz_uid: uid,
        error_status: err.response?.status,
        api_available: false,
        language: languageName
      });
      
      if (err.response && err.response.status === 404) {
        setError(
          "API endpoint not found. The quiz server may not be running. You can still fill out the form."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (e) => {
    if (!uid) return;
    
    const selectedValue = e.target.value;
    const fieldName = e.target.name;
    
    setFormData((prev) => ({
      ...prev,
      [fieldName]: selectedValue,
    }));
    
    setQuizData(null);
    setShowQuiz(false);
    setQuizResponses({});
    setCurrentCategory(0);
    setQuizProgress({});
    setFinalResponse(null);
    setIsCompleted(false);
    
    fetchQuizData(uid, selectedValue);
  };
  
  // Helper function to replace placeholders in text
  const replacePlaceholders = (text, categoryName) => {

    if (!text) return text;
    

  if (categoryName === "Ama") {
    const quizLabels = getQuizTextLabels(categoryName);
    return quizLabels.workOutPrefixAma;
  }

  return text.replace(/\{\{currentcatname\}\}/gi, categoryName || '');
};

  const getQuizTextLabels = (categoryName = '') => {
    const labels = {
      workOutPrefix: '',
      quizInstructions: '',
      questionLabel: '',
      questionLabelOf: '',
      continueToQuiz: '',
      previousCategory: '',
      previousQuestion: '',
      nextQuestion: '',
      nextCategory: '',
      submittingQuiz: '',
      submitQuiz: '',
      selectResponseTooltip: '',
      selectCountry: '',
      workOutPrefixAma: '',
      quizInstructionsByCategory: {}
    };

    const cmsList = getContentManagementsFromPayload(quizData);
    if (!cmsList) {
      return labels;
    }

    const quizTextFields = cmsList.filter(
      (field) => normalizeFormContentType(field) === "quiz-text"
    );

    quizTextFields.forEach(field => {
      const textValue = (field.description || field.title || '').trim();
      switch (field.name) {
        case 'work_out_prefix':
          labels.workOutPrefix = textValue;
          break;
        case 'work_out_prefix_ama':
          labels.workOutPrefixAma = textValue;
          break;
        case 'quiz_instructions':
          labels.quizInstructions = textValue;
          break;
        case 'question_label':
          labels.questionLabel = textValue;
          break;
        case 'question_label_of':
          labels.questionLabelOf = textValue;
          break;
        case 'continue_to_quiz':
          labels.continueToQuiz = textValue;
          break;
        case 'previous_category':
          labels.previousCategory = textValue;
          break;
        case 'previous_question':
          labels.previousQuestion = textValue;
          break;
        case 'next_question':
          labels.nextQuestion = textValue;
          break;
        case 'next_category':
          labels.nextCategory = textValue;
          break;
        case 'submitting_quiz':
          labels.submittingQuiz = textValue;
          break;
        case 'submit_quiz':
          labels.submitQuiz = textValue;
          break;
        case 'select_response_tooltip':
          labels.selectResponseTooltip = textValue;
          break;
        case 'select_country':
          labels.selectCountry = textValue;
          break;
        default:
          if (field.name && field.name.startsWith('quiz_instructions_')) {
            labels.quizInstructionsByCategory[field.name] = textValue;
          }
          break;
      }
    });

    if (categoryName) {
      const normalizedCategory = categoryName
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
      const categoryKey = `quiz_instructions_${normalizedCategory}`;
      if (labels.quizInstructionsByCategory[categoryKey]) {
        labels.quizInstructions = labels.quizInstructionsByCategory[categoryKey];
      }
    }

    return labels;
  };

  const getFormFieldsConfig = () => {
    const cmsList = getContentManagementsFromPayload(quizData);
    if (!cmsList) {
      return [];
    }

    const allowedFormTypes = [
      'text',
      'email',
      'phone',
      'date-of-birth',
      'select',
      'language',
      'privacypolicy',
      'consentcheckbox',
    ];
    
    return cmsList
      .filter((field) => allowedFormTypes.includes(normalizeFormContentType(field)))
      .sort((a, b) => {
        const tierA = formFieldSortTier(a);
        const tierB = formFieldSortTier(b);
        if (tierA !== tierB) {
          return tierA - tierB;
        }

        // Only consider sequence > 0 as having a sequence
        const aHasSequence = a.sequence !== undefined && a.sequence !== null && a.sequence > 0;
        const bHasSequence = b.sequence !== undefined && b.sequence !== null && b.sequence > 0;
        
        // If both fields have sequence > 0, sort by sequence
        if (aHasSequence && bHasSequence) {
          return a.sequence - b.sequence;
        }
        // If only one has sequence > 0, it comes first
        if (aHasSequence) {
          return -1;
        }
        if (bHasSequence) {
          return 1;
        }
        // If neither has sequence > 0 (including sequence: 0 or no sequence), sort by id (lower id first)
        const aId = a.id != null ? Number(a.id) : Infinity;
        const bId = b.id != null ? Number(b.id) : Infinity;
        return aId - bId;
      })
      .map((field) => {
        const contentType = normalizeFormContentType(field);
        const baseField = {
          type: contentType,
          name: normalizeFormFieldName(field.name),
          label: field.title || '',
          placeholder: field.description || '',
          required: field.is_required || false,
          colSpan: field.col_span || null,
          errorMessage: field.error_message || '',
        };

        if (contentType === 'language') {
          baseField.contentId = field.id;
        }

        if ((contentType === 'select' || contentType === 'language') && field.options && Array.isArray(field.options)) {
          baseField.options = field.options
            .sort((a, b) => (a.sequance || 0) - (b.sequance || 0))
            .map((opt) => {
              if (contentType === 'language') {
                return {
                  value: opt.option,
                  label: opt.option
                };
              }
              
              let value = opt.option.toLowerCase().trim();
              if (value === 'select gender' || value === '') {
                value = '';
              } else if (value === 'prefer not to say') {
                value = 'prefer_not_to_say';
              } else if (value === 'others') {
                value = 'other';
              } else {
                value = value.replace(/\s+/g, '_');
              }
              return {
                value: value,
                label: opt.option
              };
            });
        }

        if (contentType === 'date-of-birth') {
          let dynamicMonths = months;
          
          if (field.options && Array.isArray(field.options) && field.options.length > 0) {
            dynamicMonths = field.options
              .sort((a, b) => (a.sequance || 0) - (b.sequance || 0))
              .map(opt => opt.option);
          }
          
          let monthPlaceholder = 'Month';
          let dayPlaceholder = 'Day';
          let yearPlaceholder = 'Year';
          
          if (field.description) {
            const descriptionParts = field.description.split(',').map(part => part.trim());
            if (descriptionParts.length >= 3) {
              monthPlaceholder = descriptionParts[0];
              dayPlaceholder = descriptionParts[1];
              yearPlaceholder = descriptionParts[2];
            } else if (descriptionParts.length === 2) {
              monthPlaceholder = descriptionParts[0];
              dayPlaceholder = descriptionParts[1];
            } else if (descriptionParts.length === 1) {
              monthPlaceholder = descriptionParts[0];
            }
          }
          
          baseField.options = {
            months: dynamicMonths,
            days: days,
            years: years
          };
          
          baseField.placeholders = {
            month: monthPlaceholder,
            day: dayPlaceholder,
            year: yearPlaceholder
          };
        }


        if (contentType === 'privacypolicy' || contentType === 'consentcheckbox') {
          if (typeof field.urls === 'string') {
            baseField.urls = { url: field.urls };
          } else {
            baseField.urls = field.urls || {};
          }
          baseField.description = field.description || '';
        }

        return baseField;
      });
  };

  const renderFormField = (field) => {
    const {
      type,
      name,
      label,
      placeholder = '',
      required = false,
      errorKey = name, // default to field name
      errorMessage = '',
      wrapperClass = 'form-group', // default wrapper class
      colSpan = null, // default to full width
      options = []
    } = field;

    if (type === 'date-of-birth') {
      const placeholders = field.placeholders || { month: 'Month', day: 'Day', year: 'Year' };
      
      return (
        <div key={name} className={wrapperClass}>
          <label>{label}{required && <span className="required-indicator"> *</span>}</label>
          <div className="dob-row">
            <select
              name="month"
              value={formData.month}
              onChange={handleInputChange}
              required={required}
            >
              <option value="">{placeholders.month}</option>
              {options.months.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
            <select
              name="day"
              value={formData.day}
              onChange={handleInputChange}
              required={required}
            >
              <option value="">{placeholders.day}</option>
              {options.days.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
            <select
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              required={required}
            >
              <option value="">{placeholders.year}</option>
              {options.years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          {formErrors[errorKey] && <div className="field-error">{formErrors[errorKey] || errorMessage}</div>}
        </div>
      );
    }

    if (type === 'phone') {
      return (
        <div key={name} className={wrapperClass}>
          <label htmlFor={name}>{label}{required && <span className="required-indicator"> *</span>}</label>
          <input
            type="tel"
            id={name}
            name={name}
            value={formData[name]}
            onChange={handleInputChange}
            onKeyPress={(e) => {
              // Only allow numbers
              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                e.preventDefault();
              }
            }}
            placeholder={placeholder}
            required={required}
          />
          {formErrors[errorKey] && <div className="field-error">{formErrors[errorKey] || errorMessage}</div>}
        </div>
      );
    }

    if (type === 'select' || type === 'language') {
      return (
        <div key={name} className={wrapperClass}>
          <label htmlFor={name}>{label}{required && <span className="required-indicator"> *</span>}</label>
          <select
            id={name}
            name={name}
            value={formData[name] || ''}
            onChange={type === 'language' ? handleLanguageChange : handleInputChange}
            required={required}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formErrors[errorKey] && <div className="field-error">{formErrors[errorKey] || errorMessage}</div>}
        </div>
      );
    }

    if (type === 'privacypolicy' || type === 'consentcheckbox') {
      const policyDescription = placeholder || '';
      const policyUrls = field.urls || {};
      let policyUrlValue = null;
      if (typeof policyUrls === 'string') {
        policyUrlValue = policyUrls;
      } else {
        policyUrlValue = policyUrls.url || policyUrls.policy_url || null;
      }
      const policyTextContent = renderPolicyText(policyDescription, policyUrlValue);
      
      return (
        <div key={name} className={`${wrapperClass} privacy-group`}>
          <div className="checkbox-option">
            <input
              type="checkbox"
              className="privacy-consent-checkbox"
              id={name}
              name={name}
              checked={formData[name] || false}
              onChange={handleInputChange}
              required={required}
            />
            <label htmlFor={name}>
              {policyTextContent || policyDescription}
              {required && <span className="required-indicator"> *</span>}
            </label>
          </div>
          {formErrors[errorKey] && <div className="field-error">{formErrors[errorKey] || errorMessage}</div>}
        </div>
      );
    }

    return (
      <div key={name} className={wrapperClass}>
        <label htmlFor={name}>{label}{required && <span className="required-indicator"> *</span>}</label>
        <input
          type={type}
          id={name}
          name={name}
          value={formData[name] || ''}
          onChange={handleInputChange}
          required={required}
          placeholder={placeholder}
        />
        {formErrors[errorKey] && <div className="field-error">{formErrors[errorKey] || errorMessage}</div>}
      </div>
    );
  };

  const renderFormFields = () => {
    const formFieldsConfig = getFormFieldsConfig();
    
    if (!formFieldsConfig || formFieldsConfig.length === 0) {
      return null;
    }

    const fields = [];
    let currentRow = null;

    formFieldsConfig.forEach((field, index) => {
      if (field.colSpan === 'form-row') {
        if (!currentRow) {
          currentRow = [];
        }
        currentRow.push(field);

        if (index === formFieldsConfig.length - 1 || formFieldsConfig[index + 1].colSpan !== 'form-row') {
          fields.push(
            <div key={`row-${index}`} className="form-row">
              {currentRow.map(renderFormField)}
            </div>
          );
          currentRow = null;
        }
      } else {
        if (currentRow) {
          fields.push(
            <div key={`row-${index}`} className="form-row">
              {currentRow.map(renderFormField)}
            </div>
          );
          currentRow = null;
        }
        fields.push(renderFormField(field));
      }
    });

    return fields;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;    
    
    // Track form field interactions
    trackFormEvent('field_interaction', {
      field_name: name,
      field_type: type,
      field_value: type === 'checkbox' ? checked : value
    });
    
    // Track engagement when user starts filling form
    if (name === 'name' && value.length === 1) {
      trackEngagement('form_started', {
        first_field: 'name',
        form_type: 'dosha_quiz_personal_info'
      });
    }
    
    if (type === 'checkbox') {
      const formFieldsConfig = getFormFieldsConfig();
      const checkboxField = formFieldsConfig.find(
        (f) => f.name === name && POLICY_LIKE_FORM_TYPES.includes(f.type)
      );
      if (checkboxField || name === 'privacyPolicy') {
        setFormData((prev) => ({
          ...prev,
          [name]: checked,
        }));
      }
    } else {
      // For phone field, only allow numbers
      if (name === 'phone') {
        // Remove any non-numeric characters
        const numericValue = value.replace(/\D/g, '');
        setFormData((prev) => {
          const newData = {
            ...prev,
            [name]: numericValue,
          };
          return newData;
        });
      } else {
        setFormData((prev) => {
          const newData = {
            ...prev,
            [name]: value,
          };
          return newData;
        });
      }
    }

    // Clear related field errors on change
    setFormErrors((prev) => {
      const next = { ...prev };
      if (name === 'month' || name === 'day' || name === 'year') {
        delete next.dob;
      } else if (name === 'ayurvedaOther') {
        delete next.ayurvedaOther;
      } else {
        // Clear error for the field name (works for both privacyPolicy and dynamic field names)
        delete next[name];
      }
      return next;
    });
  };

  const validatePersonalInfo = () => {
    const errors = {};
    const formFieldsConfig = getFormFieldsConfig();
    
    // Validate dynamically based on content_managements
    formFieldsConfig.forEach((field) => {
      if (field.required) {
        if (field.type === 'privacypolicy' || field.type === 'consentcheckbox') {
          if (!formData[field.name]) {
            errors[field.name] =
              field.errorMessage ||
              (field.type === 'privacypolicy'
                ? 'You must accept the privacy policy.'
                : 'Please confirm to continue.');
          }
        } else if (field.type === 'date-of-birth') {
          if ((formData.month || formData.day || formData.year) && (!formData.month || !formData.day || !formData.year)) {
            errors.dob = field.errorMessage || 'Please select your full date of birth.';
          }
        } else if (field.type === 'phone') {
          // If required, check if phone is empty
          if (!formData.phone?.trim()) {
            errors.phone = field.errorMessage || 'Phone number is required.';
          }
          // No format validation needed - just numbers
        } else if (field.type === 'email') {
          if (!formData[field.name]?.trim()) {
            errors[field.name] = field.errorMessage || 'Email is required.';
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[field.name])) {
            errors[field.name] = field.errorMessage || 'Enter a valid email address.';
          }
        } else {
          if (!formData[field.name]?.trim()) {
            errors[field.name] = field.errorMessage || `${field.label || field.name} is required.`;
          }
        }
      } else {
        // Handle optional fields - no validation needed for optional phone
        // Phone field is already numbers-only, so no additional validation needed
      }
    });
    
    const isValid = Object.keys(errors).length === 0;
    trackFormEvent('form_validation', {
      is_valid: isValid,
      error_count: Object.keys(errors).length,
      error_fields: Object.keys(errors)
    });
    
    setFormErrors(errors);
    return isValid;
  };

  const handleQuizAnswer = (
    categoryName,
    questionId,
    score,
    answerText,
    question,
    answerType
  ) => {
    setQuizResponses((prev) => {
      const updated = {
        ...prev,
        [questionId]: {
          question: question,
          answer: answerText,
          answer_type: answerType,
          score: score,
        },
      };
      // Update ref immediately so it's available for maybeAdvanceCategory
      quizResponsesRef.current = updated;
      return updated;
    });
    setQuizProgress((prev) => {
      const currentProgress = prev[categoryName] || 0;
      return {
        ...prev,
        [categoryName]: currentProgress + 1,
      };
    });

    // Track quiz answer
    trackQuizEvent('question_answered', {
      category_name: categoryName,
      question_id: questionId,
      answer_score: score,
      answer_text: answerText,
      answer_type: answerType,
      question_text: question,
      category_progress: (quizProgress[categoryName] || 0) + 1
    });
  };

  const handleSubmit = async (e, status) => {
    // Get privacy policy field name dynamically
    const formFieldsConfig = getFormFieldsConfig();
    const privacyPolicyField = formFieldsConfig.find(field => field.type === 'privacypolicy');
    const privacyPolicyFieldName = privacyPolicyField?.name || 'privacyPolicy';
    
    // Track form submission attempt
    trackFormEvent('form_submission_attempt', {
      submission_status: status,
      form_data: {
        has_name: !!formData.name,
        has_email: !!formData.email,
        has_phone: !!formData.phone,
        has_dob: !!(formData.month && formData.day && formData.year),
        has_gender: !!formData.gender,
        privacy_policy_accepted: formData[privacyPolicyFieldName] || false
      }
    });

    if (status === "personal_info") {
      const isValid = validatePersonalInfo();
      if (!isValid) {
        // Track form validation failure
        trackFormEvent('form_validation_failed', {
          error_fields: Object.keys(formErrors)
        });
        try {
          const firstErrorEl = document.querySelector('.field-error');
          if (firstErrorEl) firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (_) {}
        return;
      }
      
      // Track successful form validation
      trackFormEvent('form_validation_success');
    }
    const attributionPayload = buildAttributionPayload(
      location ? { location } : null
    );
    const finalData = {
      request_type: "dosha_quiz",
      player_id: playerId,
      additional_data: {
        streaming: true,
        agent_id: agentId,
        session_id: sessionId,
        ...attributionPayload,
        email: formData.email,
        name: formData.name,
        ...(formData.phone.trim() && {
          phone: formData.phone
        }),
        ...(formData.month && formData.day && formData.year && {
          date_of_birth: `${formData.day}/${formData.month}/${formData.year}`
        }),
        ...(formData.gender && {
          gender: formData.gender
        }),
        ...(location && {
          location: location
        }),
        ...(language && {
          language: language
        }),
        privacy_policy: formData[privacyPolicyFieldName] || false,
        ...formFieldsConfig
          .filter((f) => f.type === 'consentcheckbox' && f.name)
          .reduce((acc, f) => {
            acc[f.name] = !!formData[f.name];
            return acc;
          }, {}),
        status: status,
        dosha_quiz: {},
      },
    };
    if (quizData && quizData?.data && quizData.data.categories) {
      // Use ref to get latest responses (important when called immediately after state update)
      const latestResponses = quizResponsesRef.current;
      quizData.data?.categories?.forEach((category) => {
        finalData.additional_data.dosha_quiz[category.name] =
          category.questions.map((question) => {
            const response = latestResponses[question.id] || quizResponses[question.id];
            if (response) {
              return {
                question: question.content,
                answer: question?.answer_type == 'multi_select' ? response?.answer.join(", ") : response?.answer || "",
                answer_type: question.answer_type || response.answer_type,
                score: response.score !== undefined ? response.score : 0,
              };
            } else {
              return {
                question: question.content,
                answer: "",
                answer_type: question.answer_type,
                score: 0,
              };
            }
          });
      });
    }
    const doshaScores = calculateDoshaScores();
    Object.entries(doshaScores).forEach(([name, score]) => {
      finalData.additional_data.dosha_quiz[`${name}_total_score`] =
        score.total || 0;
    });
    try {
        if (status === "completed") {

          setIsSubmitting(true);
          setIsCompleted(true);
        
        const doshaResult = getDominantDosha();
        const currentDominantDosha = doshaResult.dominantDosha;
        
        // Track quiz completion
        trackQuizEvent('quiz_completed', {
          dominant_dosha: currentDominantDosha,
          total_questions_answered: Object.keys(quizResponses).length,
          dosha_scores: doshaResult.allScores,
          completion_time: new Date().toISOString()
        });
        
        // Track conversion
        trackConversion('dosha_quiz_completion', null);
        
        try {
          navigate('/dosha-quiz/dosha-result', { 
            state: { 
              finalData,
              utmAttribution: attributionPayload.attribution || {},
              quizData: quizData,
              userInfo: {
                token: token,
                name: formData.name,
                email: formData.email,
                dominantDosha: currentDominantDosha,
                token: token,
                playerId: playerId
              }
            } 
          });
        } catch (error) {
          console.error("Error fetching products:", error);
          trackError('navigation_error', 'Failed to navigate to results page', { error: error.message });
        }
      } else {
        const response = await submitDoshaQuiz(
          finalData,
          token,
        );
        console.log("response", response);
        console.log("response?.player?.id", response?.player?.id);
        setPlayerId(response?.player?.id);
        
        // Track successful form submission
        trackFormEvent('form_submission_success', {
          player_id: response?.player?.id,
          submission_status: status
        });
        
        const categories = quizData?.data?.categories;
        if (
          quizData &&
          categories &&
          Array.isArray(categories) &&
          categories.length > 0
        ) {
          setShowQuiz(true);
          setCurrentQuestionIndex(0); 
          
          // Track page view for quiz questions
          trackPageView('Dosha Quiz Questions', window.location.pathname);
          
          // Track quiz start
          trackQuizEvent('quiz_started', {
            total_categories: categories.length,
            player_id: response?.player?.id
          });
        } else {
          setError("Quiz data not available. Please try refreshing the page.");
          trackError('quiz_data_error', 'Quiz data not available', { 
            has_quiz_data: !!quizData,
            categories_count: categories?.length || 0
          });
        }
      }
          } catch (error) {
        console.error("Error submitting quiz:", error);
        
        // Track submission error
        trackError('quiz_submission_error', error.message || "Failed to submit quiz", {
          submission_status: status,
          player_id: playerId,
          error_type: error.name || 'Unknown'
        });
      } finally {
        setIsSubmitting(false);
      } 
  };

  const generateUID = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const renderSection = (key, section) => {
    if (!section) return null;

    if (section.type) {
      switch (section.type) {
        case "heading":
          return (
            <div key={key} className="section-heading">
              <h3>{section.content}</h3>
            </div>
          );

        case "paragraph":
          return (
            <div key={key} className="section-paragraph">
              <h4>
                {key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </h4>
              <p>{section.content}</p>
            </div>
          );

        case "list":
          return (
            <div key={key} className="section-list">
              <h4>
                {section.title ||
                  key
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
              </h4>
              <ul>
                {section.content.map((item, index) => (
                  <li key={index}>{typeof item === 'object' ? JSON.stringify(item) : item}</li>
                ))}
              </ul>
            </div>
          );

        default:
          return null;
      }
    }

    // Handle nested objects (like key_characteristics, recommendations)
    if (typeof section === "object" && !Array.isArray(section)) {
      return (
        <div key={key} className="nested-section">
          <h3>
            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </h3>
          {Object.entries(section).map(([subKey, subSection]) =>
            renderSection(subKey, subSection)
          )}
        </div>
      );
    }

    // Handle arrays
    if (Array.isArray(section)) {
      return (
        <div key={key} className="array-section">
          <h4>
            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </h4>
          <ul>
            {section.map((item, index) => (
              <li key={index}>{typeof item === 'object' ? JSON.stringify(item) : item}</li>
            ))}
          </ul>
        </div>
      );
    }

    // Handle simple strings/numbers
    if (typeof section === "string" || typeof section === "number") {
      return (
        <div key={key} className="simple-section">
          <h4>
            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </h4>
          <p>{section}</p>
        </div>
      );
    }
    return null;
  };

  const renderDoshaReport = (report) => {
    if (!report || typeof report === "string") {
      return (
        <div className="raw-content">
          {report || "No report data available"}
        </div>
      );
    }

    try {
      const data = typeof report === "string" ? JSON.parse(report) : report;
      return (
        <div className="dosha-report">
          {data.report_title && (
            <h2 className="report-title">{data.report_title}</h2>
          )}

          {data.user_info && (
            <div className="user-info">
              <h4>User Information</h4>
              <p>
                <strong>Name:</strong> {data.user_info.name}
              </p>
              <p>
                <strong>Date of Birth:</strong> {data.user_info.date_of_birth}
              </p>
            </div>
          )}

          {Object.entries(data).map(([key, section]) => {
            if (["report_title", "user_info", "dosha_quiz"].includes(key))
              return null;
            if (section && typeof section === "object" && section.question)
              return null;
            return renderSection(key, section);
          })}
        </div>
      );
    } catch (error) {
      console.error("Error rendering dosha report:", error);
      return <div className="error-content">Error rendering report</div>;
    }
  };

  const renderPolicyText = (policyTextToRender, policyUrlToRender) => {
    if (!policyTextToRender) {
      return null;
    }

    const shouldCreateLink = !!policyUrlToRender;
    const regex = /<([^>]+)>(\*?)/g;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    let linkKey = 0;

    while ((match = regex.exec(policyTextToRender)) !== null) {
      const matchIndex = match.index;
      const linkText = match[1];
      const hasAsterisk = match[2] === '*';
      
      if (matchIndex > lastIndex) {
        parts.push(policyTextToRender.substring(lastIndex, matchIndex));
      }
      
      if (shouldCreateLink) {
        parts.push(
          <a
            key={`link-${linkKey++}`}
            href={policyUrlToRender}
            target="_blank"
            rel="noopener noreferrer"
            className="privacy-policy-link"
          >
            {linkText}
          </a>
        );
      } else {
        parts.push(linkText);
      }
      
      if (hasAsterisk) {
        parts.push('*');
      }
      
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < policyTextToRender.length) {
      parts.push(policyTextToRender.substring(lastIndex));
    }

    if (parts.length === 0) {
      return policyTextToRender;
    }

    return <>{parts}</>;
  };

  const getCurrentCategoryProgress = () => {
    if (
      !quizData?.data?.categories ||
      currentCategory >= quizData.data.categories.length
    ) {
      return 0;
    }

    const currentCat = quizData.data.categories[currentCategory];
    if (!currentCat || !currentCat.questions) return 0;

    let answeredCount = 0;
    currentCat.questions.forEach((question) => {
      const response = quizResponses[question.id];
      if (question.answer_type === TEXT_FIELD) {
        if (response && response.answer && response.answer.trim().length > 0) {
          answeredCount++;
        }
      } else if (question.answer_type === MULTI_SELECT) {
        if (
          response &&
          response.answer &&
          (Array.isArray(response.answer)
            ? response.answer.length > 0
            : response.answer.split(",").filter((opt) => opt.trim().length > 0)
                .length > 0)
        ) {
          answeredCount++;
        }
      } else {
        if (response && response.score !== undefined) {
          answeredCount++;
        }
      }
    });

    return answeredCount;
  };

  const calculateDoshaScores = () => {
    const scores = {};
    if (quizData?.data?.categories) {
      quizData.data.categories.forEach((category) => {
        let totalScore = 0;
        let answeredCount = 0;

        category.questions?.forEach((question) => {
          const response = quizResponses[question.id];
          if (response) {
            if (question.answer_type === TEXT_FIELD) {
              if (response.answer && response.answer.trim().length > 0) {
                totalScore += 1;
                answeredCount++;
              }
            } else if (question.answer_type === MULTI_SELECT) {
              if (response.answer) {
                let selectedCount = 0;
                if (Array.isArray(response.answer)) {
                  selectedCount = response.answer.length;
                } else {
                  selectedCount = response.answer
                    .split(",")
                    .filter((opt) => opt.trim().length > 0).length;
                }
                if (selectedCount > 0) {
                  totalScore += selectedCount;
                  answeredCount++;
                }
              }
            } else {
              if (response.score !== undefined) {
                totalScore += response.score;
                answeredCount++;
              }
            }
          }
        });

        scores[category.name.toLowerCase()] = {
          total: totalScore,
          answered: answeredCount,
          total_questions: category.questions?.length || 0,
        };
      });
    }
    return scores;
  };

  const getDominantDosha = () => {
    const doshaScores = calculateDoshaScores();
    let dominantDosha = null;
    let highestScore = -1;

    Object.entries(doshaScores).forEach(([doshaName, scoreData]) => {
      if (scoreData.total > highestScore) {
        highestScore = scoreData.total;
        dominantDosha = doshaName;
      }
    });
    setDominantDosha(dominantDosha);
    return {
      dominantDosha: dominantDosha,
      score: highestScore,
      allScores: doshaScores,
    };
  };

  const calculateFormCompletion = () => {
    const formFieldsConfig = getFormFieldsConfig();
    const requiredFields = formFieldsConfig.filter(field => field.required);

    let completedFields = 0;

    requiredFields.forEach(field => {
      if (field.type === 'privacypolicy' || field.type === 'consentcheckbox') {
        if (formData[field.name]) {
          completedFields++;
        }
      } else if (field.type === 'date-of-birth') {
        if (formData.month && formData.day && formData.year) {
          completedFields++;
        }
      } else {
        if (formData[field.name] && formData[field.name].toString().trim()) {
          completedFields++;
        }
      }
    });
    
    return requiredFields.length > 0 ? Math.round((completedFields / requiredFields.length) * 100) : 0;
  };

  const renderQuizQuestions = () => {
    const categories = quizData?.data?.categories;
    if (
      !quizData?.data ||
      !Array.isArray(categories) ||
      categories.length === 0
    ) {
      return <div>Loading quiz questions...</div>;
    }

    const currentCat = categories[currentCategory];
    if (!currentCat || !currentCat.name || !currentCat.questions) {
      return <div>Error: Invalid quiz data structure</div>;
    }

    // Helper function to render answer options based on answer_type
    const autoAdvanceToNext = (currentIndex) => {
      try {
        // Set flag to prevent scroll observer from interfering
        isManualNavigationRef.current = true;
        
        const container = document.querySelector('.quiz-full-width');
        const questionItems = document.querySelectorAll('.dosha-section .question-item');
        const nextIndex = currentIndex + 1;
        if (container && questionItems && questionItems[nextIndex]) {
          const nextEl = questionItems[nextIndex];
          nextEl.classList.add('slide-up');
          const headerEl = document.querySelector('.quiz-header');
          const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
          const containerTop = container.getBoundingClientRect().top;
          const nextTop = nextEl.getBoundingClientRect().top;
          const offsetWithinContainer = nextTop - containerTop;
          const target = Math.max(0, container.scrollTop + offsetWithinContainer - (headerHeight + 100));
          
          // Update ref immediately to prevent blinking
          if (nextIndex < currentCat.questions.length) {
            currentQuestionIndexRef.current = nextIndex;
            setCurrentQuestionIndex(nextIndex);
          }
          
          container.scrollTo({ top: target, behavior: 'smooth' });
          setTimeout(() => nextEl.classList.remove('slide-up'), 450);
          
          // Re-enable automatic tracking after scroll completes
          setTimeout(() => {
            isManualNavigationRef.current = false;
          }, 600);
        }
      } catch (e) {
        // Re-enable automatic tracking on error
        isManualNavigationRef.current = false;
      }
    };

    // Helper function to go to previous question
    const goToPreviousQuestion = (currentIndex) => {
      try {
        // Set flag to prevent scroll observer from interfering
        isManualNavigationRef.current = true;
        
        // If it's the first question of a category (but not the first category), go to previous category
        if (currentIndex === 0 && currentCategory > 0) {
          const prevCategory = categories[currentCategory - 1];
          if (prevCategory && prevCategory.questions && prevCategory.questions.length > 0) {
            // Track previous category navigation
            trackQuizEvent('category_navigation', {
              action: 'previous',
              from_category: currentCategory,
              to_category: currentCategory - 1,
              category_name: currentCat?.name
            });
            
            // Track engagement
            trackEngagement('quiz_navigation', {
              action: 'previous',
              from_category: currentCategory,
              to_category: currentCategory - 1,
              category_name: currentCat?.name
            });
            
            // Go to first question of previous category
            const newIndex = 0;
            // Update ref and state immediately to prevent blinking
            currentQuestionIndexRef.current = newIndex;
            setCurrentQuestionIndex(newIndex);
            setCurrentCategory((prev) => prev - 1);
            
            // Scroll to top after category change
            setTimeout(() => {
              const container = document.querySelector('.quiz-full-width');
              if (container) {
                container.scrollTo({ top: 0, behavior: 'smooth' });
                // Re-enable automatic tracking after scroll completes
                setTimeout(() => {
                  // Ensure index is still correct after scroll (safety check)
                  if (currentQuestionIndexRef.current !== 0) {
                    currentQuestionIndexRef.current = 0;
                    setCurrentQuestionIndex(0);
                  }
                  isManualNavigationRef.current = false;
                }, 600);
              } else {
                // If container not found, still reset the flag
                setTimeout(() => {
                  isManualNavigationRef.current = false;
                }, 600);
              }
            }, 100);
          }
        } else if (currentIndex > 0) {
          // Go to previous question in same category
          const container = document.querySelector('.quiz-full-width');
          const questionItems = document.querySelectorAll('.dosha-section .question-item');
          const prevIndex = currentIndex - 1;
          if (container && questionItems && questionItems[prevIndex]) {
            const prevEl = questionItems[prevIndex];
            const headerEl = document.querySelector('.quiz-header');
            const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
            const containerTop = container.getBoundingClientRect().top;
            const prevTop = prevEl.getBoundingClientRect().top;
            const offsetWithinContainer = prevTop - containerTop;
            const target = Math.max(0, container.scrollTop + offsetWithinContainer - (headerHeight + 100));
            
            // Update ref immediately to prevent blinking
            currentQuestionIndexRef.current = prevIndex;
            setCurrentQuestionIndex(prevIndex);
            
            container.scrollTo({ top: target, behavior: 'smooth' });
            
            // Re-enable automatic tracking after scroll completes
            setTimeout(() => {
              isManualNavigationRef.current = false;
            }, 600);
          }
        }
      } catch (e) {
        // Re-enable automatic tracking on error
        isManualNavigationRef.current = false;
      }
    };

    // Helper function to go to next question
    const goToNextQuestion = (currentIndex) => {
      try {
        // Set flag to prevent scroll observer from interfering
        isManualNavigationRef.current = true;
        
        // If it's the last question of the current category and there's a next category
        if (currentIndex === currentCat.questions.length - 1 && currentCategory < categories.length - 1) {
          const nextCategory = categories[currentCategory + 1];
          if (nextCategory && nextCategory.questions && nextCategory.questions.length > 0) {
            // Track next category navigation
            trackQuizEvent('category_navigation', {
              action: 'next',
              from_category: currentCategory,
              to_category: currentCategory + 1,
              category_name: currentCat?.name
            });
            
            // Track engagement
            trackEngagement('quiz_navigation', {
              action: 'next',
              from_category: currentCategory,
              to_category: currentCategory + 1,
              category_name: currentCat?.name
            });
            
            // Update ref immediately to prevent blinking
            currentQuestionIndexRef.current = 0;
            setCurrentCategory((prev) => prev + 1);
            setCurrentQuestionIndex(0);
            
            // Submit current category progress before moving to next
            handleSubmit(null, currentCat?.name);
            
            // Scroll to top after category change
            setTimeout(() => {
              const container = document.querySelector('.quiz-full-width');
              if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
              // Re-enable automatic tracking after scroll completes
              setTimeout(() => {
                isManualNavigationRef.current = false;
              }, 600);
            }, 100);
          }
        } else if (currentIndex < currentCat.questions.length - 1) {
          // Go to next question in same category
          autoAdvanceToNext(currentIndex);
        }
      } catch (e) {
        // Re-enable automatic tracking on error
        isManualNavigationRef.current = false;
      }
    };

    // Helper function to check if a question is answered
    const isQuestionAnswered = (question) => {
      const response = quizResponses[question.id];
      if (!response) return false;
      
      if (question.answer_type === TEXT_FIELD) {
        return response.answer && response.answer.trim().length > 0;
      } else if (question.answer_type === MULTI_SELECT) {
        if (response.answer) {
          if (Array.isArray(response.answer)) {
            return response.answer.length > 0;
          } else {
            return response.answer.split(",").filter((opt) => opt.trim().length > 0).length > 0;
          }
        }
        return false;
      } else {
        return response.score !== undefined;
      }
    };

    const maybeAdvanceCategory = (currentQuestionId = null) => {
      try {
        const categories = quizData?.data?.categories || [];
        if (!categories.length) return;
        const currentCatLocal = categories[currentCategory];
        const total = currentCatLocal?.questions?.length || 0;
        
        // Calculate progress including the current answer
        // Use ref to get the latest responses (including the one just submitted)
        const latestResponses = quizResponsesRef.current;
        let answeredCount = 0;
        currentCatLocal.questions.forEach((question) => {
          const response = latestResponses[question.id];
          // If this is the current question being answered, count it as answered
          const isCurrentQuestion = currentQuestionId && question.id === currentQuestionId;
          
          if (isCurrentQuestion) {
            answeredCount++;
          } else if (question.answer_type === TEXT_FIELD) {
            if (response && response.answer && response.answer.trim().length > 0) {
              answeredCount++;
            }
          } else if (question.answer_type === MULTI_SELECT) {
            if (
              response &&
              response.answer &&
              (Array.isArray(response.answer)
                ? response.answer.length > 0
                : response.answer.split(",").filter((opt) => opt.trim().length > 0)
                    .length > 0)
            ) {
              answeredCount++;
            }
          } else {
            if (response && response.score !== undefined) {
              answeredCount++;
            }
          }
        });
        
        if (answeredCount >= total && currentCategory < categories.length - 1) {
          // Track category completion
          trackQuizEvent('category_completed', {
            category_name: currentCatLocal?.name,
            category_index: currentCategory,
            questions_answered: answeredCount,
            total_questions: total
          });
          
          // Use setTimeout to ensure state is updated before submitting
          // Delay of 100ms gives React time to process the state update from handleQuizAnswer
          setTimeout(() => {
            // Set flag to prevent scroll observer from interfering
            isManualNavigationRef.current = true;
            
            // Update ref immediately to prevent blinking
            currentQuestionIndexRef.current = 0;
            setCurrentCategory((prev) => prev + 1);
            setCurrentQuestionIndex(0);
            
            handleSubmit(null, currentCatLocal?.name);
            setTimeout(() => {
              const container = document.querySelector('.quiz-full-width');
              if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
              // Re-enable automatic tracking after scroll completes
              setTimeout(() => {
                isManualNavigationRef.current = false;
              }, 600);
            }, 100);
          }, 100);
        }
      } catch (e) {
        // no-op
      }
    };

    const renderAnswerOptions = (question, qIndex) => {
      const { answer_type, options, id } = question;
      const opts = (answer_type === 'radio_button' && options.length === 0) ? ['0', '1', '2', '3', '4', '5', '6'] : options;
      const currentResponse = quizResponses[id];
      
      // Check if this is the last question in the last category
      const isLastQuestion = currentCategory === categories.length - 1 && qIndex === currentCat.questions.length - 1;
      if (
        !answer_type ||
        answer_type === null ||
        answer_type === undefined ||
        answer_type === ""
      ) {
        return (
          <div className="answer-options">
            <label>
              <input
                type="radio"
                name={`question_${id}`}
                value="0"
                onChange={() => {
                  handleQuizAnswer(
                    currentCat.name,
                    id,
                    0,
                    "0",
                    question.content,
                    RADIO_BUTTON
                  );
                  autoAdvanceToNext(qIndex);
                  maybeAdvanceCategory(id);
                }}
                checked={currentResponse?.score === 0}
              />
              <span>Strongly Disagree</span>
            </label>
            <label>
              <input
                type="radio"
                name={`question_${id}`}
                value="1"
                onChange={() => {
                  handleQuizAnswer(
                    currentCat.name,
                    id,
                    1,
                    "1",
                    question.content,
                    RADIO_BUTTON
                  );
                  autoAdvanceToNext(qIndex);
                  maybeAdvanceCategory(id);
                }}
                checked={currentResponse?.score === 1}
              />
              <span>&nbsp;</span>
            </label>
            <label>
              <input
                type="radio"
                name={`question_${id}`}
                value="2"
                onChange={() => {
                  handleQuizAnswer(
                    currentCat.name,
                    id,
                    2,
                    "2",
                    question.content,
                    RADIO_BUTTON
                  );
                  autoAdvanceToNext(qIndex);
                  maybeAdvanceCategory(id);
                }}
                checked={currentResponse?.score === 2}
              />
              <span>&nbsp;</span>
            </label>
            <label>
              <input
                type="radio"
                name={`question_${id}`}
                value="3"
                onChange={() => {
                  handleQuizAnswer(
                    currentCat.name,
                    id,
                    3,
                    "3",
                    question.content,
                    RADIO_BUTTON
                  );
                  autoAdvanceToNext(qIndex);
                  maybeAdvanceCategory(id);
                }}
                checked={currentResponse?.score === 3}
              />
              <span>Neutral</span>
            </label>
            <label>
              <input
                type="radio"
                name={`question_${id}`}
                value="4"
                onChange={() => {
                  handleQuizAnswer(
                    currentCat.name,
                    id,
                    4,
                    "4",
                    question.content,
                    RADIO_BUTTON
                  );
                  autoAdvanceToNext(qIndex);
                  maybeAdvanceCategory(id);
                }}
                checked={currentResponse?.score === 4}
              />
              <span>&nbsp;</span>
            </label>
            <label>
              <input
                type="radio"
                name={`question_${id}`}
                value="5"
                onChange={() => {
                  handleQuizAnswer(
                    currentCat.name,
                    id,
                    5,
                    "5",
                    question.content,
                    RADIO_BUTTON
                  );
                  autoAdvanceToNext(qIndex);
                  maybeAdvanceCategory(id);
                }}
                checked={currentResponse?.score === 5}
              />
              <span>&nbsp;</span>
            </label>
            <label>
              <input
                type="radio"
                name={`question_${id}`}
                value="6"
                onChange={() => {
                  handleQuizAnswer(
                    currentCat.name,
                    id,
                    6,
                    "6",
                    question.content,
                    RADIO_BUTTON
                  );
                  autoAdvanceToNext(qIndex);
                  maybeAdvanceCategory(id);
                }}
                checked={currentResponse?.score === 6}
              />
              <span>Strongly Agree</span>
            </label>
          </div>
        );
      }

      // If answer_type is text_field, show text input
      if (answer_type === TEXT_FIELD) {
        const hasText = !!(currentResponse?.answer && String(currentResponse.answer).trim().length > 0);
        return (
          <>
            <div className="answer-options">
              <input
                type="text"
                className="text-input"
                placeholder="Enter your answer"
                value={currentResponse?.answer || ""}
                onChange={(e) => {
                  const answerText = e.target.value;
                  handleQuizAnswer(
                    currentCat.name,
                    id,
                    answerText,
                    answerText,
                    question.content,
                    answer_type
                  );
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!hasText) return;
                    autoAdvanceToNext(qIndex);
                    maybeAdvanceCategory(id);
                  }
                }}
              />
            </div>
            {!isLastQuestion && (
              <div className={`next-link-container ${isLastQuestion ? 'last-question-nav' : ''}`}>
                <small className="help-text1">Type your answer and press Next</small>
                <p
                  className={`next-link ${hasText ? "" : "disabled"}`}
                  role="button"
                  tabIndex={0}
                  aria-disabled={!hasText}
                  onClick={() => {
                    if (!hasText) return;
                    autoAdvanceToNext(qIndex);
                    maybeAdvanceCategory(id);
                  }}
                >
                  Next
                </p>
              </div>
            )}
          </>
        );
      }

      // If answer_type is multi_select, show multi-selection checkboxes
      if (answer_type === MULTI_SELECT) {
        const hasSelection = !!(
          currentResponse?.answer && (
            Array.isArray(currentResponse.answer)
              ? currentResponse.answer.length > 0
              : currentResponse.answer
                  .split(",")
                  .filter((ans) => ans.trim() !== "").length > 0
          )
        );
        return (
          <><div className="answer-options">
            <div className="checkbox-group">
              {options &&
                options.map((option, index) => {
                  const isChecked = currentResponse?.answer
                    ? Array.isArray(currentResponse.answer)
                      ? currentResponse.answer.includes(option)
                      : currentResponse.answer.split(",").includes(option)
                    : false;
                  return (
                    <label key={index} className="checkbox-label">
                      <input
                        type="checkbox"
                        value={option}
                        checked={isChecked}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          // Handle both array and string formats for backward compatibility
                          let currentAnswers = [];
                          if (currentResponse?.answer) {
                            if (Array.isArray(currentResponse.answer)) {
                              currentAnswers = [...currentResponse.answer];
                            } else {
                              currentAnswers = currentResponse.answer
                                .split(",")
                                .filter((ans) => ans.trim() !== "");
                            }
                          }

                          let newAnswers;
                          if (isChecked) {
                            newAnswers = [...currentAnswers, option];
                          } else {
                            newAnswers = currentAnswers.filter(
                              (ans) => ans !== option
                            );
                          }
                          handleQuizAnswer(
                            currentCat.name,
                            id,
                            newAnswers.length,
                            newAnswers,
                            question.content,
                            answer_type
                          );
                        }}
                      />
                      <span className="checkbox-text">{option === '*' ? '\u00A0' : option}</span>
                    </label>
                  );
                })}
            </div>
           
          </div>
          {!isLastQuestion && (
            <div className={`next-link-container ${isLastQuestion ? 'last-question-nav' : ''}`}>
              <small className="help-text1">
                Check all options that apply to you
              </small>
              <p
                className={`next-link ${hasSelection ? "" : "disabled"}`}
                role="button"
                tabIndex={0}
                aria-disabled={!hasSelection}
                onClick={() => {
                  if (!hasSelection) return;
                  autoAdvanceToNext(qIndex);
                  maybeAdvanceCategory(id);
                }}
              >
                Next
              </p>
            </div>
          )}
         </>
        );
      }

    // If answer_type is radio, show radio buttons with given options
      if (answer_type === RADIO_BUTTON) {
        // For numeric options (0-6 scale), show all options but hide numbers for 1, 2, 4, 5
        const hasNumericOptions = opts && opts.length > 0 && opts.every(opt => /^[0-6]$/.test(String(opt)));
        
        return (
          <div className="answer-options">
            {opts && opts.length > 0 &&
              opts.map((option, index) => {
                let label;
                // Calculate score: always use index/position for all options
                const actualScore = index;
                
                // Show labels only for 0, 3, 6; hide numbers for 1, 2, 4, 5
                if (hasNumericOptions) {
                  if (option === '0') {
                    label = 'Strongly Disagree';
                  } else if (option === '3') {
                    label = 'Neutral';
                  } else if (option === '6') {
                    label = 'Strongly Agree';
                  } else {
                    label = '\u00A0'; // Non-breaking space for 1, 2, 4, 5 - maintains alignment without showing numbers
                  }
                } else {
                 
                  if (option === '*') {
                    label = '\u00A0'; 
                  } else {
                    label = option;
                  }
                }

                return (
                  <label key={index}>
                    <input
                      type="radio"
                      name={`question_${id}`}
                      value={option}
                      onChange={() => {
                        handleQuizAnswer(
                          currentCat.name,
                          id,
                          actualScore,
                          String(actualScore),
                          question.content,
                          answer_type
                        );
                        autoAdvanceToNext(qIndex);
                        maybeAdvanceCategory(id);
                      }}
                      checked={currentResponse?.score === actualScore}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
          </div>
        );
      }

      // Default fallback - show radio buttons with all options 0-6, but hide numbers for 1, 2, 4, 5
      return (
        <div className="answer-options">
          <label>
            <input
              type="radio"
              name={`question_${id}`}
              value="0"
              onChange={() => {
                handleQuizAnswer(
                  currentCat.name,
                  id,
                  0,
                  "0",
                  question.content,
                  RADIO_BUTTON
                );
                autoAdvanceToNext(qIndex);
                maybeAdvanceCategory(id);
              }}
              checked={currentResponse?.score === 0}
            />
            <span>Strongly Disagree</span>
          </label>
          <label>
            <input
              type="radio"
              name={`question_${id}`}
              value="1"
              onChange={() => {
                handleQuizAnswer(
                  currentCat.name,
                  id,
                  1,
                  "1",
                  question.content,
                  RADIO_BUTTON
                );
                autoAdvanceToNext(qIndex);
                maybeAdvanceCategory(id);
              }}
              checked={currentResponse?.score === 1}
            />
            <span></span>
          </label>
          <label>
            <input
              type="radio"
              name={`question_${id}`}
              value="2"
              onChange={() => {
                handleQuizAnswer(
                  currentCat.name,
                  id,
                  2,
                  "2",
                  question.content,
                  RADIO_BUTTON
                );
                autoAdvanceToNext(qIndex);
                maybeAdvanceCategory(id);
              }}
              checked={currentResponse?.score === 2}
            />
            <span></span>
          </label>
          <label>
            <input
              type="radio"
              name={`question_${id}`}
              value="3"
              onChange={() => {
                handleQuizAnswer(
                  currentCat.name,
                  id,
                  3,
                  "3",
                  question.content,
                  RADIO_BUTTON
                );
                autoAdvanceToNext(qIndex);
                maybeAdvanceCategory(id);
              }}
              checked={currentResponse?.score === 3}
            />
            <span>Neutral</span>
          </label>
          <label>
            <input
              type="radio"
              name={`question_${id}`}
              value="4"
              onChange={() => {
                handleQuizAnswer(
                  currentCat.name,
                  id,
                  4,
                  "4",
                  question.content,
                  RADIO_BUTTON
                );
                autoAdvanceToNext(qIndex);
                maybeAdvanceCategory(id);
              }}
              checked={currentResponse?.score === 4}
            />
            <span></span>
          </label>
          <label>
            <input
              type="radio"
              name={`question_${id}`}
              value="5"
              onChange={() => {
                handleQuizAnswer(
                  currentCat.name,
                  id,
                  5,
                  "5",
                  question.content,
                  RADIO_BUTTON
                );
                autoAdvanceToNext(qIndex);
                maybeAdvanceCategory(id);
              }}
              checked={currentResponse?.score === 5}
            />
            <span></span>
          </label>
          <label>
            <input
              type="radio"
              name={`question_${id}`}
              value="6"
              onChange={() => {
                handleQuizAnswer(
                  currentCat.name,
                  id,
                  6,
                  "6",
                  question.content,
                  RADIO_BUTTON
                );
                autoAdvanceToNext(qIndex);
                maybeAdvanceCategory(id);
              }}
              checked={currentResponse?.score === 6}
            />
            <span>Strongly Agree</span>
          </label>
        </div>
      );
    };

    const allScores = calculateDoshaScores();
    const currentScoreKey = (currentCat?.name || '').toLowerCase();
    const currentScoreData = allScores[currentScoreKey] || {
      answered: 0,
      total_questions: currentCat?.questions?.length || 0,
      total: 0,
    };

    const quizLabels = getQuizTextLabels(currentCat?.name);

    return (
      <>
        <div className="quiz-header">
           {/* Current Scores Display */}
           <div className="current-scores">
              
                <span >
                  {currentCat && currentCat.name ? currentCat.name : 'Current Category'}
                </span> : 
                {/* <span >
                  {currentScoreData.answered}/{currentScoreData.total_questions}
                </span> */}
                <span >{currentScoreData.total}</span>
              
           </div>
          <h2>{replacePlaceholders(quizLabels.workOutPrefix, currentCat.name)}</h2>
          <p className="quiz-instructions">
            {quizLabels.quizInstructions}
          </p>
          <div className="quiz-progress">
            <span>
              {currentCat.name}  {quizLabels.questionLabel || 'Question'} {currentQuestionIndex + 1} {quizLabels.questionLabelOf || 'of'} {currentCat.questions.length}
            </span>
          </div>
        </div>

        <div className={`dosha-section ${currentCategory === categories.length - 1 ? 'last-category' : ''}`}>
          {currentCat.questions.map((question, index) => {
            const isLastQuestion = currentCategory === categories.length - 1 && index === currentCat.questions.length - 1;
            const isFirstQuestionOfFirstCategory = currentCategory === 0 && index === 0;
            const isFirstQuestionOfCategory = index === 0;
            const quizLabels = getQuizTextLabels(currentCat?.name);
            
            const isLastQuestionOfCategory = index === currentCat.questions.length - 1;
            const hasNextCategory = currentCategory < categories.length - 1;
            
            return (
              <div key={question.id} className={`question-item ${isLastQuestion ? 'last-question' : ''}`}>
                 {showTooltipForQuestion === question.id && !isQuestionAnswered(question) && (
                          <div className="next-btn-tooltip">
                            {quizLabels.selectResponseTooltip || 'Please select a response to proceed'}
                          </div>
                        )} <div className="question-text">{question.content}</div>
                {renderAnswerOptions(question, index)}
                {/* Previous and Next Question/Category Buttons - Centered at Bottom */}
                {(!isFirstQuestionOfFirstCategory || !isLastQuestion) && (
                  <div className="question-navigation-btn-container">
                    {!isFirstQuestionOfFirstCategory && (
                      <button
                        className="submit-btn prev-btn"
                        onClick={() => goToPreviousQuestion(index)}
                      >
                        {isFirstQuestionOfCategory && currentCategory > 0
                          ? (quizLabels.previousCategory || 'Previous Category')
                          : (quizLabels.previousQuestion || 'Previous Question')
                        }
                      </button>
                    )}
                    {!isLastQuestion && (
                      <div 
                        className="next-btn-wrapper"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          // Check if question is answered
                          const isAnswered = isQuestionAnswered(question);
                          
                          // Check if button should be disabled for category completion
                          const isCategoryDisabled = isLastQuestionOfCategory && hasNextCategory
                            ? getCurrentCategoryProgress() < currentCat.questions.length
                            : false;
                          
                          if (!isAnswered) {
                            // Show tooltip when clicked without answer
                            setShowTooltipForQuestion(question.id);
                            // Hide tooltip after 5 seconds
                            setTimeout(() => {
                              setShowTooltipForQuestion(null);
                            }, 5000);
                          } else if (!isCategoryDisabled) {
                            //  If answered and category is complete, proceed to next
                            goToNextQuestion(index);
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          // Check if question is answered
                          const isAnswered = isQuestionAnswered(question);
                          
                          // Check if button should be disabled for category completion
                          const isCategoryDisabled = isLastQuestionOfCategory && hasNextCategory
                            ? getCurrentCategoryProgress() < currentCat.questions.length
                            : false;
                          
                          if (!isAnswered) {
                            // Show tooltip when clicked without answer
                            setShowTooltipForQuestion(question.id);
                            // Hide tooltip after 5 seconds
                            setTimeout(() => {
                              setShowTooltipForQuestion(null);
                            }, 5000);
                          } else if (!isCategoryDisabled) {
                            // If answered and category is complete, proceed to next
                            goToNextQuestion(index);
                          }
                        }}
                      >
                        <button
                          className="submit-btn next-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          disabled={
                            !isQuestionAnswered(question) ||
                            (isLastQuestionOfCategory && hasNextCategory
                              ? getCurrentCategoryProgress() < currentCat.questions.length
                              : false)
                          }
                        >
                          {isLastQuestionOfCategory && hasNextCategory
                            ? (quizLabels.nextCategory || 'Next Category')
                            : (quizLabels.nextQuestion || 'Next Question')
                          }
                        </button>
                      
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={`quiz-navigation ${currentCategory === categories.length - 1 ? 'last-category-nav' : ''}`}>
       
          {currentCategory === categories.length - 1 && (
            <button
              className="submit-btn"
              onClick={(e) => {
                // Track quiz submission
                trackQuizEvent('quiz_submission_clicked', {
                  category_name: currentCat?.name,
                  total_questions_answered: Object.keys(quizResponses).length
                });
                
                // Track engagement
                trackEngagement('quiz_submission_clicked', {
                  category_name: currentCat?.name,
                  total_questions_answered: Object.keys(quizResponses).length,
                  final_category: currentCategory
                });
                
                handleSubmit(e, "completed");
              }}
              disabled={
                getCurrentCategoryProgress() < currentCat.questions.length || isSubmitting
              }
            >
              {isSubmitting ? (
                <>
                  <div className="loading-spinner-small"></div>
                  {getQuizTextLabels().submittingQuiz || 'Submitting Quiz...'}
                </>
              ) : (
                getQuizTextLabels().submitQuiz || "Submit Quiz"
              )}
            </button>
          )}
        </div>
      </>
     
  


    );
  };

  // Dynamic title and meta tags based on quiz data
  const pageTitle = quizData?.data?.title || 'Know Your Dominant Dosha & Start Your Ayurvedic Wellness Journey Today';
  const pageDescription = quizData?.data?.description || 'Take the Ayurvedic Dosha Quiz and get instant insights into your unique body type. Understand your Ama (toxin buildup), receive diet and lifestyle guidance, and explore personalized remedies to restore balance.';
  const LINK_PREVIEW_TITLE = 'Know Your Dominant Dosha & Start Your Ayurvedic Wellness Journey Today';
  const LINK_PREVIEW_DESCRIPTION =
    'Take the Ayurvedic Dosha Quiz and get instant insights into your unique body type. Understand your Ama (toxin buildup), receive diet and lifestyle guidance, and explore personalized remedies to restore balance.';
  const DEFAULT_PREVIEW_IMAGE = 'https://maharishiayurvedaindia.com/images/dosha-quiz-preview.jpg';
  const ogImageUrl = quizData?.data?.og_image || DEFAULT_PREVIEW_IMAGE;
  const quizFormLeftImage = QUIZ_FORM_LEFT_IMAGE_BY_UID[uid];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={LINK_PREVIEW_TITLE} />
        <meta property="og:description" content={LINK_PREVIEW_DESCRIPTION} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={LINK_PREVIEW_TITLE} />
        <meta name="twitter:description" content={LINK_PREVIEW_DESCRIPTION} />
        <meta name="twitter:image" content={ogImageUrl} />
        <link rel="icon" href="https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://maharishiayurvedaindia.com&size=64" />
      </Helmet>
      <div className="dosha-quiz-container">
        {!showQuiz ? (
      <div
        className={`quiz-layout${quizFormLeftImage ? " quiz-layout--custom-image" : ""}`}
      >
        {/* Left Branding Section */}
        <div
          className={`quiz-left${quizFormLeftImage ? " quiz-left--custom-image" : ""}`}
        >
          {quizFormLeftImage && (
            <img
              src={quizFormLeftImage}
              alt="Dosha Quiz"
              className="quiz-left-image"
            />
          )}
        </div>

        {/* Right Form Section */}
        <div className="quiz-right">
          <div className="form-card">
            {loading ? (
              <div className="loading-message">
                <div className="loading-spinner-small"></div>
                <p>Loading...</p>
              </div>
            ) : (
              <>
                {error && (
                  <div className="error-message">
                    <p>{error}</p>
                    {!apiAvailable && (
                      <p style={{ fontSize: "12px", opacity: 0.8 }}>
                        {" "}
                        Note: You can still complete the form without API
                        connection.{" "}
                      </p>
                    )}
                    <button
                      onClick={() => window.location.reload()}
                      className="retry-btn"
                    >
                      {" "}
                      Retry{" "}
                    </button>
                  </div>
                )}

                <form className="quiz-form">
                  {/* Dynamically rendered form fields (including language) */}
                  {renderFormFields()}

                  <button
                    type="button"
                    className="submit-btn"
                    onClick={(e) => {
                      // Track form submission button click
                      trackFormEvent('form_submit_button_clicked', {
                        button_text: 'Continue to Quiz',
                        form_completion_percentage: calculateFormCompletion()
                      });
                      
                      // Track engagement
                      trackEngagement('continue_to_quiz_clicked', {
                        form_completion_percentage: calculateFormCompletion(),
                        has_name: !!formData.name,
                        has_email: !!formData.email,
                        has_phone: !!formData.phone
                      });
                      
                      handleSubmit(e, "personal_info");
                    }}
                  >
                    {" "}
                    {getQuizTextLabels().continueToQuiz || "Continue to Quiz"}{" "}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
       ) : (
        <>
          {" "}
            {!finalResponse && (
          <div className="quiz-full-width">
            {" "}
            {loading ? (
              <div className="loading-message">
                Loading quiz questions...
              </div>
            ) : quizData && quizData.data ? (
              renderQuizQuestions()
            ) : (
              <div className="error-message">
                <p>
                  Quiz data not available. Please try refreshing the
                  page.
                </p>{" "}
                <button
                  onClick={() => window.location.reload()}
                  className="retry-btn"
                >
                  {" "}
                  Retry{" "}
                </button>{" "}
              </div>
            )}{" "}
          </div>
        )}{" "}  
        </>
      )}
      </div>
    </>
  );
};

export default DoshaQuiz;
